import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Types
import { PiSuggestedTopic, PiSuggestedTopicType } from 'Shared/types/PiSuggestedTopic.type';
import { ItemType } from 'Shared/constants/types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useTrackPiSuggestedTopics } from 'Shared/tracking/use-track-pi-suggested-topics';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Actions
import { addItem } from 'Shared/actions/todos';

// Components
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpText } from 'Wpds/components/text';
import { WpBadge } from 'Wpds/components/badge';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './PiSuggestedTopicsListItem.scss';

type Props = {
  topic: PiSuggestedTopic;
};

const PiSuggestedTopicsListItem = ({ topic }: Props) => {
  const { trackTopicAdded } = useTrackPiSuggestedTopics();

  const dispatch = useDispatch();
  const history = useHistory();
  const { id: workspaceId } = useWorkspace();

  const [isSaving, toggleIsSaving] = useToggle(false);
  const [hasAdded, toggleHasAdded] = useToggle(false);

  const handleViewBtnClick = (workspaceId, itemUuid) => {
    history.push(PageUrlsUtil.makeWorkspaceMeetingItemCommentsUrl(workspaceId, itemUuid));
  };

  const handleAddToWorkspace = () => {
    toggleIsSaving();

    const itemUuid = uuidv4();
    return dispatch(
      addItem({
        itemUuid,
        body: topic.suggestion,
        workspaceId: workspaceId,
        position: 1,
        itemType: ItemType.discussionTopic,
      })
    ).then(() => {
      toggleIsSaving();
      trackTopicAdded({ suggestion: topic.suggestion, category: topic.category });
      WpToastManager.success('Topic created', {
        actionBtnLabel: 'view',
        actionBtnFnc: () => handleViewBtnClick(workspaceId, itemUuid),
      });
      toggleHasAdded();
    });
  };

  const baseClassName = 'PiSuggestedTopicsListItem';

  return (
    <>
      <WpContainer color="gray" className={baseClassName}>
        <WpContainerBody>
          <div className={`${baseClassName}__body`}>
            <div className={`${baseClassName}__body__details`}>
              <WpText noMargin>{topic.suggestion}</WpText>
              <div className={`${baseClassName}__body__details__meta`}>
                <WpBadge color="blue" size="sm">
                  {topic.category}
                </WpBadge>
                {(topic.type === PiSuggestedTopicType.factor ||
                  topic.type === PiSuggestedTopicType.factorCombination) && (
                  <WpText noMargin variant="bodyXs" color="gray-600">
                    <WpText bold noMargin variant="bodyXs" color="gray-600" tag="span">
                      Based On:
                    </WpText>
                    &nbsp;{topic.basedOn}
                  </WpText>
                )}
              </div>
            </div>
            <div>
              {hasAdded ? (
                <div className={`${baseClassName}__body__added`}>
                  <WpIcon name="check-circle" prefix="fas" color={ColorConstants.purple[800]} />
                  Added
                </div>
              ) : (
                <WpTooltip>
                  <WpTooltipTrigger>
                    <WpButton variant="secondary" size="sm" onClick={handleAddToWorkspace} loading={isSaving}>
                      <WpIcon name="plus" />
                    </WpButton>
                  </WpTooltipTrigger>
                  <WpTooltipContent>Add to agenda</WpTooltipContent>
                </WpTooltip>
              )}
            </div>
          </div>
        </WpContainerBody>
      </WpContainer>
    </>
  );
};

export default PiSuggestedTopicsListItem;
